import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Luggage, Plus, Trash2, Star, AlertTriangle, BookOpenCheck, X, CheckCircle2, Bell, Baby } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { SECTIONS, TRIP_TYPES } from "../../data/packingChecklist";
import { AGE_GROUPS, AGE_GROUP_LABEL } from "../../data/familyPacking";

/* Per-trip packing list (My Travel Hub): items with packed checkbox (auto-dates),
   location, category, notes, critical flag. Intelligent warnings: power banks /
   critical documents in checked baggage, unpacked critical items. Items can be
   added free-form or pulled in from the interactive Packing Guide. */

export const LOCATIONS = ["On Person", "Purse / Personal Item", "Carry-on", "Checked Bag 1",
  "Checked Bag 2", "Checked Bag 3", "Vehicle", "Hotel / Destination", "Shipped Separately", "Other"];
const CHECKED_LOCATIONS = ["Checked Bag 1", "Checked Bag 2", "Checked Bag 3", "Shipped Separately"];
export const CATEGORIES = ["Documents", "Money", "Clothing", "Health/Medicine", "Electronics",
  "Toiletries", "Food/Water", "Children", "Pet", "Emergency", "Other"];

const SECTION_CATEGORY = { documents: "Documents", health: "Health/Medicine", meds: "Health/Medicine",
  hydration: "Food/Water", emergency: "Emergency", weather: "Clothing", comfort: "Other",
  electronics: "Electronics", toiletries: "Toiletries", kit24: "Toiletries", vision: "Health/Medicine",
  security: "Documents", children: "Children", pets: "Pet", road: "Emergency", cruise: "Other" };

const CRITICAL_RE = /passport|visa\b|license|\bid\b|medicat|medicine|prescription|wallet|phone\b|keys\b|travel documents|medical (device|equipment|supplies)|boarding pass/i;
const LITHIUM_RE = /power bank|lithium|battery pack|spare batter|vape|e-cig/i;

export const itemWarning = (it) => {
  if (!CHECKED_LOCATIONS.includes(it.location)) return null;
  if (LITHIUM_RE.test(it.label))
    return "Power banks and spare lithium batteries must NOT go in checked baggage — move this to your cabin/personal item and keep it accessible.";
  if (it.critical || CRITICAL_RE.test(it.label))
    return "Important documents and critical items should stay accessible to you — consider a secure personal pouch instead of checked baggage.";
  return null;
};

const defaultItem = (label, category = "Other", critical = false) => ({
  id: `pi-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  label, category, location: "", packed: false, packed_at: null, notes: "", critical });

export default function TripPacking({ tripId, tripStartDate, readOnly, people = [], travelers = [] }) {
  const [doc, setDoc] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const [familyOpen, setFamilyOpen] = useState(false);
  const [locFilter, setLocFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [personFilter, setPersonFilter] = useState("");
  const [unpackedOnly, setUnpackedOnly] = useState(false);
  const [reminder, setReminder] = useState(null);
  const [reminderDays, setReminderDays] = useState(1);
  const saveTimer = useRef(null);

  useEffect(() => {
    api.get(`/packing/trip/${tripId}`).then((r) => setDoc(r.data)).catch(() => {});
    api.get(`/reminders/entity/trip_packing/${tripId}`)
      .then((r) => {
        const items = r.data.upcoming || r.data.items || (Array.isArray(r.data) ? r.data : []);
        setReminder(items.find((x) => ["scheduled", "snoozed"].includes(x.status)) || null);
      }).catch(() => {});
  }, [tripId]);

  const persist = useCallback((next) => {
    setDoc(next);
    if (readOnly) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put(`/packing/trip/${tripId}`, { items: next.items,
        travel_completed: next.travel_completed, completed_date: next.completed_date })
        .catch(() => toast.error("Could not save packing list"));
    }, 600);
  }, [tripId, readOnly]);

  const updateItem = (id, patch) => persist({ ...doc, items: doc.items.map((i) => i.id === id ? { ...i, ...patch } : i) });
  const togglePacked = (it) => updateItem(it.id, { packed: !it.packed,
    packed_at: !it.packed ? new Date().toISOString().slice(0, 10) : null });
  const removeItem = (id) => persist({ ...doc, items: doc.items.filter((i) => i.id !== id) });
  const addItem = () => {
    const label = newLabel.trim();
    if (!label) return;
    persist({ ...doc, items: [...doc.items, defaultItem(label, "Other", CRITICAL_RE.test(label))] });
    setNewLabel("");
  };
  const setCompleted = (yes) => persist({ ...doc, travel_completed: yes,
    completed_date: yes ? new Date().toISOString().slice(0, 10) : null });

  const stats = useMemo(() => {
    const items = doc?.items || [];
    const packed = items.filter((i) => i.packed).length;
    const criticalUnpacked = items.filter((i) => (i.critical || CRITICAL_RE.test(i.label)) && !i.packed);
    const warnings = items.map((i) => ({ item: i, warning: itemWarning(i) })).filter((w) => w.warning);
    const byPerson = {};
    for (const i of items) {
      if (!i.owner) continue;
      const p = byPerson[i.owner] || (byPerson[i.owner] = { total: 0, packed: 0 });
      p.total += 1;
      if (i.packed) p.packed += 1;
    }
    return { total: items.length, packed, pct: items.length ? Math.round((packed / items.length) * 100) : 0,
      criticalUnpacked, warnings, byPerson };
  }, [doc]);

  if (!doc) return null;

  const matchesFilters = (it) =>
    (!locFilter || it.location === locFilter) &&
    (!catFilter || it.category === catFilter) &&
    (!personFilter || (personFilter === "__unclaimed" ? !it.owner : it.owner === personFilter)) &&
    (!unpackedOnly || !it.packed);
  const visibleItems = doc.items.filter(matchesFilters);
  const filtering = locFilter || catFilter || unpackedOnly || personFilter;
  const packers = [...new Set(["Me", ...people, ...doc.items.map((i) => i.owner).filter(Boolean)])];

  const setPackingReminder = async () => {
    try {
      const r = await api.post(`/packing/trip/${tripId}/reminder`, { days_before: reminderDays });
      setReminder(r.data);
      toast.success(`Reminder set for ${r.data.remind_at.slice(0, 10)} — it will list anything still unpacked`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set reminder"); }
  };
  const cancelReminder = async () => {
    try { await api.delete(`/reminders/${reminder.id}`); setReminder(null); toast.success("Reminder cancelled"); }
    catch { toast.error("Could not cancel"); }
  };

  return (
    <section className="mb-10" data-testid="trip-packing">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h2 className="font-heading font-700 text-xl flex items-center gap-2"><Luggage className="w-5 h-5 text-[#0e7490]" /> Packing List</h2>
        {!readOnly && (
          <div className="flex gap-2">
            <button onClick={() => setFamilyOpen((f) => !f)} data-testid="trip-packing-family-btn"
              className={`inline-flex items-center gap-1.5 border px-3 py-2 text-sm font-700 ${familyOpen ? "bg-[#7c3aed] border-[#7c3aed] text-white" : "border-[#7c3aed] text-[#7c3aed] hover:bg-violet-50"}`}>
              <Baby className="w-4 h-4" /> Family Packing
            </button>
            <button onClick={() => setGuideOpen(true)} data-testid="trip-packing-guide-btn"
              className="inline-flex items-center gap-1.5 border border-[#0e7490] text-[#0e7490] px-3 py-2 text-sm font-700 hover:bg-cyan-50">
              <BookOpenCheck className="w-4 h-4" /> Add From Packing Guide
            </button>
          </div>
        )}
      </div>

      {!readOnly && familyOpen && (
        <FamilyPanel travelers={travelers} existing={doc.items}
          onAdd={(items) => {
            persist({ ...doc, items: [...doc.items, ...items] });
            toast.success(`${items.length} age-based item${items.length > 1 ? "s" : ""} added`);
          }} />
      )}

      {stats.total > 0 && (
        <div className="border border-zinc-300 bg-white px-4 py-3 mb-3" data-testid="trip-packing-progress">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2.5 bg-zinc-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${stats.pct}%` }} /></div>
            <span className="text-sm font-700 whitespace-nowrap" data-testid="trip-packing-progress-text">{stats.packed} of {stats.total} packed — {stats.pct}%</span>
          </div>
          <div className="mt-1.5 text-xs font-700" data-testid="trip-packing-status">
            {stats.criticalUnpacked.length > 0
              ? <span className="text-rose-700">🔴 {stats.criticalUnpacked.length} critical item{stats.criticalUnpacked.length > 1 ? "s" : ""} not packed yet: {stats.criticalUnpacked.slice(0, 4).map((i) => i.label).join(", ")}{stats.criticalUnpacked.length > 4 ? "…" : ""}</span>
              : stats.packed === stats.total
                ? <span className="text-emerald-700">🟢 Everything packed!</span>
                : <span className="text-amber-700">🟠 {stats.total - stats.packed} item{stats.total - stats.packed > 1 ? "s" : ""} still need attention — all critical items packed</span>}
          </div>
          {Object.keys(stats.byPerson).length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="trip-packing-person-chips">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Who packs what:</span>
              {Object.entries(stats.byPerson).map(([name, p]) => (
                <button key={name} onClick={() => setPersonFilter(personFilter === name ? "" : name)}
                  data-testid={`trip-packing-person-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`text-[11px] font-700 border px-2 py-0.5 ${personFilter === name ? "bg-[#0e7490] border-[#0e7490] text-white" : p.packed === p.total ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-zinc-300 text-zinc-700"}`}>
                  {name} {p.packed}/{p.total}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {stats.warnings.length > 0 && (
        <div className="border border-rose-400 bg-rose-50 p-3 mb-3 space-y-1.5" data-testid="trip-packing-warnings">
          {stats.warnings.map(({ item, warning }) => (
            <div key={item.id} className="flex gap-2 text-sm text-rose-900">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span><span className="font-700">{item.label}</span> ({item.location}): {warning}</span>
            </div>
          ))}
        </div>
      )}

      {doc.items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 text-sm" data-testid="trip-packing-filters">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Show:</span>
          <select value={locFilter} onChange={(e) => setLocFilter(e.target.value)} data-testid="trip-packing-filter-location"
            className="border border-zinc-300 bg-white px-2 py-1 text-xs">
            <option value="">All locations</option>
            {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="trip-packing-filter-category"
            className="border border-zinc-300 bg-white px-2 py-1 text-xs">
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 text-xs font-600 cursor-pointer">
            <input type="checkbox" checked={unpackedOnly} onChange={(e) => setUnpackedOnly(e.target.checked)}
              data-testid="trip-packing-filter-unpacked" className="w-3.5 h-3.5 accent-[#0e7490]" />
            Not packed only
          </label>
          <select value={personFilter} onChange={(e) => setPersonFilter(e.target.value)} data-testid="trip-packing-filter-person"
            className="border border-zinc-300 bg-white px-2 py-1 text-xs">
            <option value="">Everyone</option>
            <option value="__unclaimed">Unclaimed items</option>
            {packers.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {filtering && (
            <>
              <span className="text-xs text-muted-foreground" data-testid="trip-packing-filter-count">{visibleItems.length} of {doc.items.length} items</span>
              <button onClick={() => { setLocFilter(""); setCatFilter(""); setUnpackedOnly(false); }}
                data-testid="trip-packing-filter-clear" className="text-xs font-700 text-[#0e7490] hover:underline">Clear</button>
            </>
          )}
        </div>
      )}

      <div className="border border-zinc-300 bg-white divide-y divide-zinc-100" data-testid="trip-packing-items">
        {doc.items.length === 0 && (
          <p className="p-4 text-sm text-muted-foreground">No items yet — add your own below or pull recommendations from the Packing Guide.</p>
        )}
        {doc.items.length > 0 && visibleItems.length === 0 && (
          <p className="p-4 text-sm text-muted-foreground" data-testid="trip-packing-filter-empty">No items match these filters.</p>
        )}
        {visibleItems.map((it) => (
          <div key={it.id} className="p-3 flex flex-wrap items-center gap-2" data-testid={`trip-packing-item-${it.id}`}>
            <input type="checkbox" checked={it.packed} onChange={() => togglePacked(it)} disabled={readOnly}
              data-testid={`trip-packing-check-${it.id}`} className="w-4 h-4 accent-[#0e7490] shrink-0" />
            <button onClick={() => !readOnly && updateItem(it.id, { critical: !it.critical })} disabled={readOnly}
              data-testid={`trip-packing-critical-${it.id}`} title={it.critical ? "Critical item" : "Mark as critical"}>
              <Star className={`w-4 h-4 ${it.critical || CRITICAL_RE.test(it.label) ? "fill-rose-500 text-rose-500" : "text-zinc-300"}`} />
            </button>
            <span className={`flex-1 min-w-[140px] text-sm font-600 ${it.packed ? "text-zinc-400 line-through" : "text-zinc-800"}`}>{it.label}</span>
            <select value={LOCATIONS.includes(it.location) ? it.location : (it.location ? "Other" : "")}
              onChange={(e) => updateItem(it.id, { location: e.target.value === "Other" ? (it.location && !LOCATIONS.includes(it.location) ? it.location : "Other") : e.target.value })}
              disabled={readOnly} data-testid={`trip-packing-location-${it.id}`}
              className={`border px-2 py-1 text-xs bg-white ${itemWarning(it) ? "border-rose-400 text-rose-700" : "border-zinc-300 text-zinc-700"}`}>
              <option value="">Location…</option>
              {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select value={it.owner || ""} onChange={(e) => updateItem(it.id, { owner: e.target.value || null })} disabled={readOnly}
              data-testid={`trip-packing-owner-${it.id}`} title="Who packs this item"
              className={`border px-2 py-1 text-xs bg-white ${it.owner ? "border-[#0e7490] text-[#0e7490] font-700" : "border-zinc-200 text-zinc-400"}`}>
              <option value="">Who packs?</option>
              {packers.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={it.category} onChange={(e) => updateItem(it.id, { category: e.target.value })} disabled={readOnly}
              className="border border-zinc-200 px-2 py-1 text-xs bg-white text-zinc-500 hidden sm:block">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {it.packed && (
              <input type="date" value={it.packed_at || ""} onChange={(e) => updateItem(it.id, { packed_at: e.target.value })}
                disabled={readOnly} data-testid={`trip-packing-date-${it.id}`}
                className="border border-zinc-200 px-1.5 py-1 text-xs text-zinc-500 bg-white" />
            )}
            <input value={it.notes} onChange={(e) => updateItem(it.id, { notes: e.target.value })} disabled={readOnly}
              placeholder="Notes…" data-testid={`trip-packing-notes-${it.id}`}
              className="border border-zinc-200 px-2 py-1 text-xs w-32 lg:w-44 bg-white" />
            {!readOnly && (
              <button onClick={() => removeItem(it.id)} data-testid={`trip-packing-remove-${it.id}`}
                className="text-zinc-300 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
        {!readOnly && (
          <div className="p-3 flex gap-2">
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()}
              placeholder="Add an item — e.g. Passport, Toothbrush, Travel adapter…" data-testid="trip-packing-add-input"
              className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:outline-none" />
            <button onClick={addItem} data-testid="trip-packing-add-btn"
              className="inline-flex items-center gap-1 bg-[#0e7490] text-white px-3 py-2 text-sm font-700 hover:bg-cyan-700"><Plus className="w-4 h-4" /> Add</button>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-3 text-sm" data-testid="trip-packing-completed">
        <span className="font-700 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Travel completed:</span>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={!doc.travel_completed} onChange={() => setCompleted(false)} disabled={readOnly}
            data-testid="trip-packing-completed-no" className="accent-[#0e7490]" /> No
        </label>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={!!doc.travel_completed} onChange={() => setCompleted(true)} disabled={readOnly}
            data-testid="trip-packing-completed-yes" className="accent-[#0e7490]" /> Yes
        </label>
        {doc.travel_completed && doc.completed_date && (
          <span className="text-xs text-muted-foreground" data-testid="trip-packing-completed-date">Completed {doc.completed_date} — your packing list is kept as part of this trip's record.</span>
        )}
      </div>

      {!readOnly && !doc.travel_completed && (
        <div className="mt-3 border border-zinc-200 bg-zinc-50 px-4 py-3 flex flex-wrap items-center gap-2 text-sm" data-testid="trip-packing-reminder">
          <Bell className="w-4 h-4 text-[#0e7490] shrink-0" />
          {reminder ? (
            <>
              <span className="font-600" data-testid="trip-packing-reminder-set">
                Pre-trip packing reminder set for <span className="font-700">{reminder.remind_at.slice(0, 10)}</span> — it will email you anything still unpacked (critical items first).
              </span>
              <button onClick={cancelReminder} data-testid="trip-packing-reminder-cancel"
                className="text-xs font-700 text-rose-600 hover:underline">Cancel</button>
            </>
          ) : tripStartDate ? (
            <>
              <span className="font-600">Remind me</span>
              <select value={reminderDays} onChange={(e) => setReminderDays(Number(e.target.value))}
                data-testid="trip-packing-reminder-days" className="border border-zinc-300 bg-white px-2 py-1 text-xs">
                {[[0, "on departure day"], [1, "1 day before"], [2, "2 days before"], [3, "3 days before"], [7, "1 week before"]]
                  .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <span className="font-600">departure with anything still unpacked</span>
              <button onClick={setPackingReminder} data-testid="trip-packing-reminder-btn"
                className="inline-flex items-center gap-1 bg-[#0e7490] text-white px-3 py-1.5 text-xs font-700 hover:bg-cyan-700">
                <Bell className="w-3.5 h-3.5" /> Set reminder
              </button>
            </>
          ) : (
            <span className="text-muted-foreground">Add a travel date to this trip to schedule a pre-departure packing reminder.</span>
          )}
        </div>
      )}

      {guideOpen && <GuideModal onClose={() => setGuideOpen(false)} existing={doc.items}
        onAdd={(items) => { persist({ ...doc, items: [...doc.items, ...items] }); setGuideOpen(false); toast.success(`${items.length} item${items.length > 1 ? "s" : ""} added to your trip`); }} />}
    </section>
  );
}

/* Family Travel Tool — auto-detects kids from linked travellers (age_group derived
   from DOB server-side, the date itself stays masked) and suggests age-appropriate
   packing items. Groups can also be picked manually for kids without a DOB. */
function FamilyPanel({ travelers, existing, onAdd }) {
  const kids = travelers.filter((t) => ["infant", "toddler", "child", "teen"].includes(t.age_group));
  const [selectedKids, setSelectedKids] = useState(() => Object.fromEntries(kids.map((k) => [k.id, true])));
  const [manualGroups, setManualGroups] = useState({});

  const targets = [
    ...kids.filter((k) => selectedKids[k.id]).map((k) => ({ group: k.age_group, owner: k.name || null })),
    ...AGE_GROUPS.filter((g) => manualGroups[g.id]).map((g) => ({ group: g.id, owner: null })),
  ];
  const existingKeys = new Set(existing.map((i) => `${i.label.toLowerCase()}|${(i.owner || "").toLowerCase()}`));
  const pending = [];
  for (const t of targets) {
    const group = AGE_GROUPS.find((g) => g.id === t.group);
    if (!group) continue;
    for (const label of group.items) {
      const key = `${label.toLowerCase()}|${(t.owner || "").toLowerCase()}`;
      if (existingKeys.has(key)) continue;
      existingKeys.add(key);
      pending.push({ ...defaultItem(label, "Children", CRITICAL_RE.test(label)), owner: t.owner });
    }
  }

  return (
    <div className="border border-[#7c3aed]/40 bg-violet-50/40 p-4 mb-3" data-testid="trip-packing-family-panel">
      <div className="text-xs font-mono uppercase tracking-widest text-[#7c3aed] font-700 mb-2">Family Travel — packing by age</div>

      {kids.length > 0 ? (
        <>
          <div className="text-xs text-zinc-600 mb-1.5">Kids detected on this trip (from travellers' dates of birth):</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {kids.map((k) => (
              <button key={k.id} onClick={() => setSelectedKids((s) => ({ ...s, [k.id]: !s[k.id] }))}
                data-testid={`family-kid-${k.id}`}
                className={`px-2.5 py-1 text-xs font-700 border ${selectedKids[k.id] ? "bg-[#7c3aed] border-[#7c3aed] text-white" : "bg-white border-zinc-300 text-zinc-700"}`}>
                {k.name} · {AGE_GROUP_LABEL[k.age_group]}
              </button>
            ))}
          </div>
        </>
      ) : (
        <p className="text-xs text-zinc-600 mb-2">
          No kids auto-detected — add a date of birth to your child travellers to detect their ages automatically, or pick age groups below.
        </p>
      )}

      <div className="text-xs text-zinc-600 mb-1.5">{kids.length > 0 ? "Also add for an age group (no DOB needed):" : "Pick the age groups travelling with you:"}</div>
      <div className="flex flex-wrap gap-2 mb-3">
        {AGE_GROUPS.map((g) => (
          <button key={g.id} onClick={() => setManualGroups((s) => ({ ...s, [g.id]: !s[g.id] }))}
            data-testid={`family-group-${g.id}`}
            className={`px-2.5 py-1 text-xs font-700 border ${manualGroups[g.id] ? "bg-[#7c3aed] border-[#7c3aed] text-white" : "bg-white border-zinc-300 text-zinc-700"}`}>
            {g.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={() => pending.length && onAdd(pending)} disabled={!pending.length}
          data-testid="family-add-items-btn"
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-700 ${pending.length ? "bg-[#7c3aed] text-white hover:bg-violet-700" : "bg-zinc-200 text-zinc-400 cursor-not-allowed"}`}>
          <Plus className="w-4 h-4" /> Add {pending.length || ""} suggested item{pending.length === 1 ? "" : "s"}
        </button>
        {targets.length > 0 && pending.length === 0 && (
          <span className="text-xs text-emerald-700 font-600" data-testid="family-all-added">All suggestions for this selection are already on your list.</span>
        )}
      </div>
      <p className="mt-2 text-[11px] text-zinc-500">Items added for a detected kid are automatically assigned to them in "Who packs?".</p>
    </div>
  );
}

function GuideModal({ onClose, onAdd, existing }) {
  const [types, setTypes] = useState([]);
  const [selected, setSelected] = useState({});
  const typeSet = new Set(types);
  const existingLabels = new Set(existing.map((i) => i.label.toLowerCase()));

  const sections = SECTIONS
    .filter((s) => !s.tags || s.tags.some((t) => typeSet.has(t)))
    .map((s) => ({ ...s, items: s.items.filter((i) => !i.tags.length || i.tags.some((t) => typeSet.has(t))) }))
    .filter((s) => s.items.length > 0);

  const toggleType = (t) => setTypes((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t]);
  const count = Object.values(selected).filter(Boolean).length;

  const add = () => {
    const items = [];
    for (const s of sections) {
      for (const i of s.items) {
        if (selected[i.id] && !existingLabels.has(i.label.toLowerCase())) {
          items.push({ ...defaultItem(i.label, SECTION_CATEGORY[s.id] || "Other", CRITICAL_RE.test(i.label)) });
        }
      }
    }
    if (!items.length) { toast.info("Select at least one new item"); return; }
    onAdd(items);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="trip-packing-guide-modal">
      <div className="bg-white border border-zinc-300 w-full max-w-3xl my-8">
        <div className="flex items-center justify-between px-5 py-3 bg-[#0e7490] text-white sticky top-0">
          <span className="font-heading font-900">Add Items From the Packing Guide</span>
          <button onClick={onClose} data-testid="trip-packing-guide-close" className="p-1 hover:bg-cyan-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Trip type (optional — reveals more recommendations)</span>
          <div className="flex flex-wrap gap-2 mt-2 mb-4">
            {TRIP_TYPES.map(([t, label]) => (
              <button key={t} onClick={() => toggleType(t)} data-testid={`guide-type-${t}`}
                className={`px-2.5 py-1 text-xs font-700 border ${typeSet.has(t) ? "bg-[#0e7490] border-[#0e7490] text-white" : "bg-white border-zinc-300 text-zinc-700"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 max-h-[50vh] overflow-y-auto pr-1">
            {sections.map((s) => (
              <div key={s.id}>
                <div className="text-xs font-900 uppercase tracking-wide text-zinc-500 mb-1">{s.title}</div>
                {s.items.map((i) => {
                  const already = existingLabels.has(i.label.toLowerCase());
                  return (
                    <label key={i.id} className={`flex items-start gap-2 text-sm py-0.5 ${already ? "text-zinc-300" : "text-zinc-800 cursor-pointer"}`}>
                      <input type="checkbox" disabled={already} checked={!!selected[i.id]}
                        onChange={() => setSelected((sel) => ({ ...sel, [i.id]: !sel[i.id] }))}
                        data-testid={`guide-item-${i.id}`} className="w-4 h-4 mt-0.5 accent-[#0e7490] shrink-0" />
                      <span>{i.label}{already && <span className="text-[10px] ml-1">(already on your list)</span>}</span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-4 border-t border-zinc-200 mt-4">
            <button onClick={add} data-testid="guide-add-selected-btn"
              className="bg-emerald-700 text-white px-5 py-2.5 text-sm font-700 hover:bg-emerald-600">
              Add {count > 0 ? `${count} ` : ""}Selected Item{count === 1 ? "" : "s"} to My Trip
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
